// Remembers books that failed to open, so we never resume into one.
//
// 2026-09-28: a 40 MB EPUB (Rock*) opened to a cover and a progress bar and
// then stopped. Because PageVoice resumes into the most recently opened book
// on launch, that single bad file made the WHOLE APP look broken from the
// Home Screen — every launch landed on the same dead screen with no way out
// except knowing to reach for the library. One bad import should be a minor
// annoyance, not a brick.
//
// localStorage rather than Dexie on purpose: this has to be readable during
// BootRedirect, before any Dexie schema upgrade has necessarily settled, and
// it must survive a Dexie wipe. It is a hint, never authoritative — the marker
// is cleared the moment the book renders a section.

const PREFIX = "pv.openFailed.";

/** How long a failure keeps a book out of the resume slot. After this the
 *  book gets another chance on launch — a failure can be transient (low
 *  memory, a half-synced blob), and we don't want to exile a book forever. */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function markOpenFailed(bookId: string | undefined): void {
  if (!bookId) return;
  try {
    localStorage.setItem(PREFIX + bookId, String(Date.now()));
  } catch {
    /* storage full or blocked — the watchdog still shows the error screen */
  }
}

export function clearOpenFailed(bookId: string | undefined): void {
  if (!bookId) return;
  try {
    localStorage.removeItem(PREFIX + bookId);
  } catch {
    /* no-op */
  }
}

/** True if this book failed to open recently and should not be auto-resumed.
 *  Opening it explicitly from the library is always still allowed. */
export function didOpenFail(bookId: string | undefined): boolean {
  if (!bookId) return false;
  try {
    const raw = localStorage.getItem(PREFIX + bookId);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    if (Date.now() - at > TTL_MS) {
      localStorage.removeItem(PREFIX + bookId);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}
